/**
 * The Generate index: the owner's generation jobs from the cloud, newest run first, a page of
 * whole runs at a time. The cloud is the record, so the index survives a reload; jobs made on
 * this page are put on top without waiting for a reload. A job that feeds a template (the
 * editor's view generation) belongs to that template, not here — unless its clear image waits
 * for a person: closing the editor must never leave it with no way to decide.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { GenerationJobView, JsonClient } from "@genz/platform";
import { errorDetail } from "./errorCode.js";

/** Runs per page; the cloud sends every job of each. */
export const INDEX_PAGE_SIZE = 20;

function merged(rows: GenerationJobView[], next: GenerationJobView[], onTop: boolean): GenerationJobView[] {
  const ids = new Set(next.map((j) => j.id));
  const rest = rows.filter((j) => !ids.has(j.id));
  return onTop ? [...next, ...rest] : [...rest, ...next];
}

export function useGenerationIndex(client: JsonClient) {
  const [rows, setRows] = useState<GenerationJobView[]>([]);
  const [hasMore, setHasMore] = useState(false);
  /** Where the cloud said the next page of runs starts. */
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (before?: string) => {
    setError(null);
    try {
      const { jobs, nextBefore } = await client.listGenerationRuns({ limit: INDEX_PAGE_SIZE, before });
      setRows((prev) => merged(prev, jobs.filter((j) => !j.templateId || j.status === "clear_review"), false));
      setCursor(nextBefore ?? undefined);
      setHasMore(nextBefore !== null);
    } catch (e) {
      setError(errorDetail(e));
    }
  }, [client]);

  useEffect(() => { void loadPage(); }, [loadPage]);

  const loadMore = () => void loadPage(cursor);
  /** The first page again, on top: runs another device made or moved since this one loaded.
   *  A failure keeps what is shown; the next return asks again. */
  const reloadTop = useCallback(async () => {
    try {
      const { jobs } = await client.listGenerationRuns({ limit: INDEX_PAGE_SIZE });
      setRows((prev) => merged(prev, jobs.filter((j) => !j.templateId || j.status === "clear_review"), true));
    } catch {
      /* kept as shown */
    }
  }, [client]);
  const putOnTop = useCallback((jobs: GenerationJobView[]) => setRows((prev) => merged(prev, jobs, true)), []);
  const replace = useCallback(
    (job: GenerationJobView) => setRows((prev) => prev.map((j) => (j.id === job.id ? job : j))), []);
  const remove = useCallback((id: string) => setRows((prev) => prev.filter((j) => j.id !== id)), []);
  /** The row as the cloud has it now, with result links signed afresh. */
  const refresh = useCallback(async (id: string) => {
    const job = await client.getGenerationJob(id);
    replace(job);
    return job;
  }, [client, replace]);

  return { rows, hasMore, error, loadMore, retry: () => void loadPage(), reloadTop, putOnTop, replace, remove, refresh };
}

/**
 * A result link is signed for minutes while the index keeps its rows for hours, so an image that
 * fails to load asks for its row again: once, until an image of that row loads.
 */
export function useExpiredLinkRefresh(refresh: (id: string) => Promise<unknown>) {
  const asked = useRef(new Set<string>());
  const onError = useCallback((id: string) => {
    if (asked.current.has(id)) return;
    asked.current.add(id);
    refresh(id).catch(() => undefined);
  }, [refresh]);
  const onLoad = useCallback((id: string) => { asked.current.delete(id); }, []);
  return { onError, onLoad };
}
